import { Button, Popover, Tooltip } from 'antd'
import type { TooltipPlacement } from 'antd/es/tooltip'
import {
  cloneElement,
  useCallback,
  useRef,
  useState,
  type HTMLAttributes,
  type ReactElement,
  type ReactNode,
} from 'react'
import { useTranslation } from 'react-i18next'

export type VistoeraActionConfirmTone = 'danger' | 'default'

export interface VistoeraActionConfirmProps {
  /** Icon button or other click trigger — must be a single React element. */
  children: ReactElement
  title: string
  description?: ReactNode
  confirmLabel?: string
  cancelLabel?: string
  tone?: VistoeraActionConfirmTone
  placement?: TooltipPlacement
  /** Hover label for icon-only triggers; suppressed while the popover is open. */
  tooltip?: string
  disabled?: boolean
  confirmDisabled?: boolean
  loading?: boolean
  onConfirm: () => void | Promise<void>
  onOpenChange?: (open: boolean) => void
}

const DEFAULT_PLACEMENT: TooltipPlacement = 'bottomRight'

/**
 * Anchored destructive / lightweight confirmation for icon-button triggers.
 * Replaces centered `Modal.confirm` for delete/remove flows initiated from
 * small icons — popup stays beside the trigger with auto placement flip.
 *
 * VISTOERA_SAAS_I18N_FOUNDATION V1 (spec §31) — `confirmLabel`/`cancelLabel`
 * default to the shared `actions:confirm`/`actions:cancel` translation keys
 * (follows the current UI Locale automatically) instead of a hardcoded
 * Chinese literal, so every caller that doesn't pass its own override
 * — most still do, for now (legacy debt, not migrated this round) — still
 * gets a correctly-localized button once it stops passing an explicit
 * Chinese string. `title`/`description`/`tooltip` remain 100% the calling
 * Surface's responsibility (domain-specific copy, spec §31's "Domain-
 * specific confirm label override") — this component intentionally has no
 * opinion on those.
 */
export default function VistoeraActionConfirm({
  children,
  title,
  description,
  confirmLabel,
  cancelLabel,
  tone = 'danger',
  placement = DEFAULT_PLACEMENT,
  tooltip,
  disabled = false,
  confirmDisabled = false,
  loading: externalLoading = false,
  onConfirm,
  onOpenChange,
}: VistoeraActionConfirmProps) {
  const { t } = useTranslation('actions')
  const resolvedConfirmLabel = confirmLabel ?? t('confirm')
  const resolvedCancelLabel = cancelLabel ?? t('cancel')
  const [open, setOpen] = useState(false)
  const [internalLoading, setInternalLoading] = useState(false)
  const loading = externalLoading || internalLoading
  // Synchronous re-entrancy guard — `internalLoading` (React state) only
  // takes effect on the NEXT render, so two clicks dispatched within the
  // same tick (fast double-click, or any programmatic double-submit) would
  // both read the pre-update `loading=false` closure and both pass the
  // guard below. A plain ref mutates immediately, closing that race
  // (VISTOERA_GLOBAL_ICON_ACTION_POPOVER_V1 §24 "mutation 只发一次" /
  // DOUBLE_SUBMIT_PRESENT = NO) without waiting on any render cycle.
  const submittingRef = useRef(false)

  const setOpenState = useCallback(
    (next: boolean) => {
      if (loading && !next) return
      setOpen(next)
      onOpenChange?.(next)
    },
    [loading, onOpenChange],
  )

  const handleConfirm = async () => {
    if (loading || confirmDisabled || submittingRef.current) return
    submittingRef.current = true
    try {
      setInternalLoading(true)
      await onConfirm()
      setOpen(false)
      onOpenChange?.(false)
    } catch {
      /* caller shows toast; keep popover open */
    } finally {
      submittingRef.current = false
      setInternalLoading(false)
    }
  }

  const panel = (
    <div className="vistoera-action-confirm" data-testid="action-confirm">
      <div className="vistoera-action-confirm-title">{title}</div>
      {description ? <div className="vistoera-action-confirm-desc">{description}</div> : null}
      <div className="vistoera-action-confirm-actions">
        <Button
          size="small"
          onClick={() => setOpenState(false)}
          disabled={loading}
          data-testid="action-confirm-cancel"
        >
          {resolvedCancelLabel}
        </Button>
        <Button
          size="small"
          type="primary"
          danger={tone === 'danger'}
          loading={loading}
          disabled={confirmDisabled}
          onClick={() => void handleConfirm()}
          data-testid="action-confirm-submit"
        >
          {resolvedConfirmLabel}
        </Button>
      </div>
    </div>
  )

  const trigger = cloneElement(children as ReactElement<HTMLAttributes<HTMLElement>>, {
    'aria-haspopup': 'dialog',
    'aria-expanded': open,
  })

  const anchored = (
    <Popover
      open={open && !disabled}
      onOpenChange={(next) => {
        if (!disabled) setOpenState(next)
      }}
      trigger="click"
      placement={placement}
      arrow={{ pointAtCenter: true }}
      destroyOnHidden
      content={panel}
      overlayClassName="vistoera-action-popover-overlay"
      data-testid="action-popover"
    >
      <span className="vistoera-action-confirm-trigger" onClick={(e) => e.stopPropagation()}>
        {trigger}
      </span>
    </Popover>
  )

  if (!tooltip) return anchored

  return (
    <Tooltip title={tooltip} open={open ? false : undefined}>
      {anchored}
    </Tooltip>
  )
}
